import type { ReactNode } from "react";
import { Badge, Box, Checkbox, Group, Loader, Paper, Stack, Text, ThemeIcon, Tooltip } from "@mantine/core";
import { Check, Sparkles } from "lucide-react";

/**
 * How a step gets done:
 * - `manual` — the member ticks it;
 * - `action` — the embedded form completes it (no tick box);
 * - `auto` — the server detects it (no tick box).
 */
export type GuideChecklistStepKind = "manual" | "action" | "auto";

export interface GuideChecklistStep {
  key: string;
  title: string;
  done: boolean;
  kind: GuideChecklistStepKind;
  /** A company step: shared by the company's admins, shown only to them. */
  company?: boolean;
  /** An important step (it reopens a finished guide until it is done). */
  important?: boolean;
  /** Who ticked a company step — omitted when unknown. */
  doneBy?: string | null;
}

export interface GuideChecklistProps {
  steps: GuideChecklistStep[];
  /** Called when a `manual` step is ticked or unticked. Omit for a read-only list. */
  onToggle?: (step: GuideChecklistStep, done: boolean) => void;
  /** The step whose change is being saved: its tick box shows a spinner and is disabled. */
  pendingKey?: string | null;
  /** The step's content, rendered under its title (markdown, an FAQ article, a live block…). */
  renderBody?: (step: GuideChecklistStep) => ReactNode;
}

function StepMarker({
  step,
  index,
  onToggle,
  pending,
}: {
  step: GuideChecklistStep;
  index: number;
  onToggle?: GuideChecklistProps["onToggle"];
  pending: boolean;
}) {
  if (pending) {
    return (
      <ThemeIcon size={24} radius="xl" variant="light" color="gray" aria-label="Saving">
        <Loader size={14} />
      </ThemeIcon>
    );
  }
  if (step.kind === "manual" && onToggle) {
    return (
      <Checkbox
        size="md"
        radius="xl"
        checked={step.done}
        onChange={(e) => onToggle(step, e.currentTarget.checked)}
        aria-label={step.done ? `Mark "${step.title}" as not done` : `Mark "${step.title}" as done`}
      />
    );
  }
  if (step.done) {
    return (
      <ThemeIcon size={24} radius="xl" color="teal" aria-label="Done">
        <Check size={14} />
      </ThemeIcon>
    );
  }
  const hint = step.kind === "auto" ? "Ticks itself once it's done" : step.kind === "action" ? "Done once you complete it below" : "";
  return (
    <Tooltip label={hint} disabled={!hint} withArrow>
      <ThemeIcon size={24} radius="xl" variant="light" color="gray" aria-label={hint || "Not done"}>
        <Text fz="xs" fw={600}>
          {index + 1}
        </Text>
      </ThemeIcon>
    </Tooltip>
  );
}

/**
 * A guide's steps as a checklist: one bordered row per step with its tick box
 * or status marker, title, badges (company step, important, detected
 * automatically) and — via `renderBody` — the step's content underneath.
 * Presentational only: the caller saves ticks and passes `pendingKey` while it
 * does.
 */
export function GuideChecklist({ steps, onToggle, pendingKey = null, renderBody }: GuideChecklistProps) {
  if (steps.length === 0) {
    return (
      <Text fz="sm" c="dimmed">
        This guide has no steps yet.
      </Text>
    );
  }
  return (
    <Stack gap="sm" component="ol" m={0} p={0} style={{ listStyle: "none" }}>
      {steps.map((step, index) => {
        const body = renderBody?.(step);
        return (
          <Paper
            key={step.key}
            component="li"
            withBorder
            radius="md"
            p="md"
            data-done={step.done || undefined}
            style={step.done ? { borderColor: "var(--mantine-color-teal-outline)" } : undefined}
          >
            <Group align="flex-start" wrap="nowrap" gap="sm">
              <Box pt={2}>
                <StepMarker step={step} index={index} onToggle={onToggle} pending={pendingKey === step.key} />
              </Box>
              <Stack gap="xs" flex={1} miw={0}>
                <Group gap="xs" wrap="wrap">
                  <Text fw={600}>
                    {step.title}
                  </Text>
                  {step.company && (
                    <Badge size="xs" variant="light" color="violet">
                      Company step
                    </Badge>
                  )}
                  {step.important && !step.done && (
                    <Badge size="xs" variant="light" color="orange" leftSection={<Sparkles size={10} />}>
                      Important
                    </Badge>
                  )}
                  {step.kind === "auto" && (
                    <Badge size="xs" variant="outline" color="gray">
                      {step.done ? "Detected" : "Detected automatically"}
                    </Badge>
                  )}
                </Group>
                {step.company && step.done && step.doneBy && (
                  <Text fz="xs" c="dimmed">
                    Done by {step.doneBy}
                  </Text>
                )}
                {body ? <Box>{body}</Box> : null}
              </Stack>
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
